import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Tells somebody using a screen reader that the page changed.
 *
 * <h2>The gap this closes</h2>
 *
 * A full page load moves focus to the top of the new document and the browser
 * announces its title. A single-page application does neither: React swaps the
 * DOM, focus stays wherever it was — often on a link that no longer exists — and
 * nothing is announced. Somebody navigating by keyboard or screen reader has no
 * idea anything happened, and their next Tab continues from a position on a page
 * that is gone.
 *
 * <p>Almost every SPA has this bug, because nothing visible is wrong.
 *
 * <h2>Announce, do not steal focus</h2>
 *
 * The title goes into a live region rather than being focused. Moving focus on
 * every navigation fights anybody who deliberately focused something, and
 * announcing is what a browser does on a real page load.
 *
 * <p>The region is emptied before it is filled: assistive technology announces a
 * change in content, so navigating twice to pages with the same title would
 * otherwise be silent the second time.
 */
export function RouteAnnouncer() {
  const location = useLocation();
  const region = useRef<HTMLParagraphElement>(null);
  const firstRender = useRef(true);

  useEffect(() => {
    // Not on the initial render: the browser already announced the document it
    // loaded, and repeating it is noise before anybody has done anything.
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const element = region.current;
    if (!element) {
      return;
    }

    element.textContent = '';
    const announced = window.setTimeout(() => {
      element.textContent = `${document.title}, page loaded`;
    }, 100);

    return () => {
      window.clearTimeout(announced);
    };
  }, [location.pathname]);

  return (
    <p
      ref={region}
      // An explicit role as well as aria-live. They agree -- role="alert" implies
      // assertive -- and the role is what assistive technology that does not act
      // on a bare live region still recognises. Assertive rather than polite
      // because the person just navigated: this is the answer to something they
      // did, not background chatter.
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      // Visually hidden but reachable: `display: none` is not announced, and
      // `visibility: hidden` is not either.
      className="sr-only"
    />
  );
}
